/**
 * V3 — Employés. CRUD complet sur stationROPS.employees.
 * Lecture + Création + Modification + Suppression avec audit + sync legacy.
 */
import React, { useEffect, useState } from 'react';
import { api } from './api';
import { Users, Briefcase, Wallet, Phone, Mail, Plus, Pencil, Trash2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');

const EMPTY_FORM = {
  nom: '',
  poste: '',
  salaire: '',
  date_embauche: '',
  telephone: '',
  email: '',
  statut: 'Actif',
};

export default function V3Employees() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [toast, setToast] = useState(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.resolve().then(async () => {
      try {
        const { data: d } = await api.get('/api/v3/employees');
        if (alive) setData(d);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message);
      }
    });
    return () => { alive = false; };
  }, [reloadTick]);

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (emp) => {
    setEditingId(emp.id);
    setFormData({
      nom: emp.nom || '',
      poste: emp.poste || '',
      salaire: emp.salaire ? String(emp.salaire) : '',
      date_embauche: emp.date_embauche || '',
      telephone: emp.telephone || '',
      email: emp.email || '',
      statut: emp.statut || 'Actif',
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormError(null);
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!formData.nom.trim()) { setFormError('Le nom est obligatoire.'); return; }
    setBusy(true);
    setFormError(null);
    try {
      const payload = {
        nom: formData.nom.trim(),
        poste: formData.poste.trim() || null,
        salaire: Number(formData.salaire || 0),
        date_embauche: formData.date_embauche || null,
        telephone: formData.telephone.trim() || null,
        email: formData.email.trim() || null,
        statut: formData.statut || 'Actif',
      };
      if (editingId) {
        await api.put(`/api/v3/employees/${editingId}`, payload);
        showToast(`Employé « ${payload.nom} » mis à jour`);
      } else {
        await api.post('/api/v3/employees', payload);
        showToast(`Employé « ${payload.nom} » créé`);
      }
      closeForm();
      setReloadTick((t) => t + 1);
    } catch (e) {
      setFormError(e?.response?.data?.detail || e.message || 'Erreur réseau');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    setBusy(true);
    try {
      const emp = data.items.find((x) => x.id === confirmDeleteId);
      await api.delete(`/api/v3/employees/${confirmDeleteId}`);
      showToast(`« ${emp?.nom || 'Employé'} » supprimé`);
      setConfirmDeleteId(null);
      setReloadTick((t) => t + 1);
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur suppression', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="v3-error">⚠️ {err}</div>;
  if (!data) return <div className="v3-skeleton">Chargement…</div>;

  return (
    <div className="v3-page" data-testid="v3-employees">
      <div className="v3-page-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1>Employés</h1>
          <p>{data.total} employé{data.total > 1 ? 's' : ''} · masse salariale {fmt(data.kpis.masse_salariale)} FCFA</p>
        </div>
        <button
          onClick={openCreate}
          className="v3-btn v3-btn-primary"
          data-testid="employees-new-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Plus size={16} /> Nouvel employé
        </button>
      </div>

      <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: 700 }}>
        <KpiCard icon={<Users />} label="Effectif" value={data.kpis.effectif} accent="cyan" />
        <KpiCard icon={<Wallet />} label="Masse salariale" value={fmt(data.kpis.masse_salariale) + ' FCFA'} accent="amber" />
      </section>

      <section className="v3-grid-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {data.items.length === 0 && <div className="v3-empty">Aucun employé enregistré.</div>}
        {data.items.map((e) => (
          <div key={e.id || e.nom} className="v3-card v3-emp-card" data-testid={`employee-card-${e.id}`}>
            <div className="v3-card-body">
              <div className="v3-emp-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <div className="v3-emp-avatar">{(e.nom || '?').slice(0, 2).toUpperCase()}</div>
                  <div className="v3-emp-id" style={{ minWidth: 0, flex: 1 }}>
                    <div className="v3-emp-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.nom || 'Sans nom'}</div>
                    <div className="v3-emp-role"><Briefcase size={11} /> {e.poste || '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => openEdit(e)}
                    title="Modifier"
                    aria-label="Modifier"
                    data-testid={`edit-employee-${e.id}`}
                    className="v3-icon-btn"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(e.id)}
                    title="Supprimer"
                    aria-label="Supprimer"
                    data-testid={`delete-employee-${e.id}`}
                    className="v3-icon-btn v3-icon-btn-danger"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="v3-emp-meta">
                {e.salaire > 0 && (
                  <div className="v3-emp-meta-row">
                    <span><Wallet size={12} /> Salaire</span>
                    <strong>{fmt(e.salaire)} FCFA</strong>
                  </div>
                )}
                {e.telephone && (
                  <div className="v3-emp-meta-row">
                    <span><Phone size={12} /> Tél</span>
                    <strong className="mono">{e.telephone}</strong>
                  </div>
                )}
                {e.email && (
                  <div className="v3-emp-meta-row">
                    <span><Mail size={12} /> Email</span>
                    <strong className="mono" style={{ fontSize: 11 }}>{e.email}</strong>
                  </div>
                )}
                {e.date_embauche && (
                  <div className="v3-emp-meta-row">
                    <span>Embauche</span>
                    <strong className="mono">{e.date_embauche}</strong>
                  </div>
                )}
              </div>
              <div className="v3-emp-status">
                <span className={`v3-pill ${e.statut === 'Actif' ? 'v3-pill-live' : 'v3-pill-out'}`}>{e.statut || 'Actif'}</span>
              </div>
            </div>
          </div>
        ))}
      </section>

      {data.pompistes_noms && data.pompistes_noms.length > 0 && (
        <section className="v3-card" style={{ marginTop: 22 }}>
          <div className="v3-card-head">
            <h2>Pompistes</h2>
            <span className="v3-card-sub">Référence depuis le journal</span>
          </div>
          <div className="v3-card-body">
            <div className="v3-pompiste-grid">
              {data.pompistes_noms.map((p, i) => (
                <div key={i} className="v3-pompiste-chip">{p}</div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Modal de création/édition */}
      {showForm && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}>
          <form className="v3-modal" onSubmit={submitForm} data-testid="employee-form">
            <div className="v3-modal-head">
              <h2>{editingId ? 'Modifier un employé' : 'Nouvel employé'}</h2>
              <button type="button" onClick={closeForm} aria-label="Fermer" className="v3-icon-btn"><X size={16} /></button>
            </div>
            <div className="v3-modal-body">
              <FormField label="Nom complet *">
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Eric MOMBO"
                  required
                  data-testid="employee-form-nom"
                  autoFocus
                />
              </FormField>
              <FormField label="Poste">
                <input
                  type="text"
                  value={formData.poste}
                  onChange={(e) => setFormData({ ...formData, poste: e.target.value })}
                  placeholder="Ex: Pompiste, Gérant…"
                  data-testid="employee-form-poste"
                />
              </FormField>
              <div className="v3-form-row">
                <FormField label="Salaire (FCFA)">
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.salaire}
                    onChange={(e) => setFormData({ ...formData, salaire: e.target.value })}
                    placeholder="0"
                    data-testid="employee-form-salaire"
                  />
                </FormField>
                <FormField label="Statut">
                  <select
                    value={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                    data-testid="employee-form-statut"
                  >
                    <option value="Actif">Actif</option>
                    <option value="Inactif">Inactif</option>
                    <option value="Suspendu">Suspendu</option>
                  </select>
                </FormField>
              </div>
              <div className="v3-form-row">
                <FormField label="Date d'embauche">
                  <input
                    type="date"
                    value={formData.date_embauche}
                    onChange={(e) => setFormData({ ...formData, date_embauche: e.target.value })}
                    data-testid="employee-form-date"
                  />
                </FormField>
                <FormField label="Téléphone">
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="+241..."
                    data-testid="employee-form-tel"
                  />
                </FormField>
              </div>
              <FormField label="Email pro">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="prenom.nom@grouper-ops.com"
                  data-testid="employee-form-email"
                />
              </FormField>
              {formError && (
                <div className="v3-form-error">
                  <AlertTriangle size={14} /> {formError}
                </div>
              )}
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={closeForm} className="v3-btn v3-btn-secondary" data-testid="employee-form-cancel">Annuler</button>
              <button type="submit" disabled={busy} className="v3-btn v3-btn-primary" data-testid="employee-form-submit">
                {busy ? 'Enregistrement…' : (editingId ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation suppression */}
      {confirmDeleteId && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDeleteId(null); }}>
          <div className="v3-modal v3-modal-confirm">
            <div className="v3-modal-body" style={{ textAlign: 'center' }}>
              <AlertTriangle size={36} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
              <h2 style={{ marginBottom: 6 }}>Confirmer la suppression</h2>
              <p style={{ color: '#94a3b8', fontSize: 13 }}>
                {(() => {
                  const e = data.items.find(x => x.id === confirmDeleteId);
                  return `Supprimer l'employé « ${e?.nom || confirmDeleteId} » ? Cette action est synchronisée avec l'app legacy.`;
                })()}
              </p>
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={() => setConfirmDeleteId(null)} className="v3-btn v3-btn-secondary">Annuler</button>
              <button type="button" onClick={confirmDelete} disabled={busy} className="v3-btn v3-btn-danger" data-testid="confirm-delete-btn">
                {busy ? 'Suppression…' : 'Oui, supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`v3-toast v3-toast-${toast.type}`} data-testid="employee-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{toast.text}</span>
        </div>
      )}

      <style>{`
        .v3-btn {
          padding: 10px 18px;
          border-radius: 10px;
          border: 0;
          font: 600 13px 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: all .2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .v3-btn-primary {
          background: linear-gradient(135deg, #D6F32F, #84cc16);
          color: #0a1628;
        }
        .v3-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(214,243,47,0.25);
        }
        .v3-btn-primary:disabled { opacity: .5; cursor: wait; }
        .v3-btn-secondary {
          background: rgba(255,255,255,0.06);
          color: #e5e7eb;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .v3-btn-secondary:hover { background: rgba(255,255,255,0.1); }
        .v3-btn-danger {
          background: rgba(239,68,68,0.15);
          color: #fca5a5;
          border: 1px solid rgba(239,68,68,0.4);
        }
        .v3-btn-danger:hover { background: rgba(239,68,68,0.25); }

        .v3-icon-btn {
          width: 30px; height: 30px;
          border-radius: 8px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: #94a3b8;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all .2s;
        }
        .v3-icon-btn:hover {
          background: rgba(214,243,47,0.1);
          border-color: rgba(214,243,47,0.3);
          color: #D6F32F;
        }
        .v3-icon-btn-danger:hover {
          background: rgba(239,68,68,0.1);
          border-color: rgba(239,68,68,0.3);
          color: #fca5a5;
        }

        .v3-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(10,22,40,0.85);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 20px;
        }
        .v3-modal {
          background: #0f1e3a;
          border: 1px solid rgba(214,243,47,0.15);
          border-radius: 18px;
          max-width: 540px; width: 100%;
          color: #e5e7eb;
          box-shadow: 0 30px 80px rgba(0,0,0,0.6);
          max-height: 90vh;
          display: flex; flex-direction: column;
        }
        .v3-modal-confirm { max-width: 420px; }
        .v3-modal-head {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .v3-modal-head h2 {
          font-size: 17px; font-weight: 800; color: #fff;
          letter-spacing: -0.3px; margin: 0;
        }
        .v3-modal-body { padding: 24px; overflow-y: auto; }
        .v3-modal-foot {
          display: flex; gap: 10px; justify-content: flex-end;
          padding: 16px 24px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .v3-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        .v3-form-error {
          margin-top: 14px;
          padding: 12px 14px;
          background: rgba(239,68,68,0.1);
          border: 1px solid rgba(239,68,68,0.3);
          color: #fca5a5;
          font-size: 12px;
          border-radius: 8px;
          display: flex; align-items: center; gap: 8px;
        }
        .v3-form-field {
          margin-bottom: 16px;
        }
        .v3-form-field-label {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1.2px;
          color: #71717a;
          font-weight: 700;
          margin-bottom: 6px;
        }
        .v3-form-field input,
        .v3-form-field select {
          width: 100%;
          padding: 12px 14px;
          background: rgba(0,0,0,0.3);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #fff;
          font: 500 13px Inter;
          transition: all .2s;
        }
        .v3-form-field input:focus,
        .v3-form-field select:focus {
          outline: none;
          border-color: #D6F32F;
          background: rgba(0,0,0,0.4);
          box-shadow: 0 0 0 3px rgba(214,243,47,0.12);
        }

        .v3-toast {
          position: fixed;
          bottom: 24px; right: 24px;
          padding: 14px 20px;
          background: #0f1e3a;
          color: #fff;
          border-radius: 12px;
          display: inline-flex; align-items: center; gap: 10px;
          font: 600 13px Inter;
          z-index: 2000;
          box-shadow: 0 12px 32px rgba(0,0,0,0.5);
          animation: v3-toast-in .3s ease-out;
        }
        @keyframes v3-toast-in { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .v3-toast-success {
          border: 1px solid rgba(34,197,94,0.4);
          color: #86efac;
        }
        .v3-toast-error {
          border: 1px solid rgba(239,68,68,0.4);
          color: #fca5a5;
        }
      `}</style>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="v3-form-field">
      <label className="v3-form-field-label">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
      </div>
    </div>
  );
}
